/**
 * Intelligent WhatsApp routing helper.
 *
 * The backend's `resolve_provider()` may return one of three response shapes:
 *
 *   • Auto-sent (Business API / Demo)      → `{status:"sent", mode:"api"|"demo", ...}`
 *   • Manual deep-link (Business App)      → `{status:"queued_manual", mode:"app", deep_link:"https://wa.me/..."}`
 *   • Not connected (mock/mocked)          → `{status:"mocked", mode:"none"|"demo_expired", ...}`
 *
 * This helper normalises those into a single UX behaviour used across
 * every "Send" flow in the app.
 *
 *   `openWhatsAppOutcome(response, { onEmailFallback })`
 *
 * Returns a boolean so callers can chain toasts appropriately.
 */
import { toast } from "sonner";

export function findWhatsAppResult(response) {
  // Endpoints wrap the WhatsApp result under different keys. Search common shapes.
  if (!response || typeof response !== "object") return null;
  if (response.mode || response.deep_link) return response;
  for (const k of ["whatsapp", "wa", "wa_result", "whatsapp_result", "result"]) {
    if (response[k] && (response[k].mode || response[k].deep_link)) return response[k];
  }
  return null;
}

export function openWhatsAppOutcome(response, opts = {}) {
  const r = findWhatsAppResult(response) || response || {};
  const mode = r.mode || "unknown";

  if (mode === "app" && r.deep_link) {
    // Open in a new tab so the user's existing app context is preserved.
    // wa.me will attempt the desktop app, WhatsApp Web, or the mobile app.
    window.open(r.deep_link, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp Business — press Send to deliver.");
    return true;
  }

  if (mode === "api" || (mode === "demo" && r.status !== "mocked")) {
    toast.success(mode === "demo" ? "Sent via Viyora Demo WhatsApp" : "WhatsApp sent");
    return true;
  }

  if (mode === "demo_expired") {
    toast.error("Free trial ended — connect your Business App or API to send.");
    return false;
  }

  if (mode === "none" || r.status === "mocked" || r.status === "skipped") {
    toast.warning("No WhatsApp connected — send via Email instead?", {
      action: opts.onEmailFallback ? { label: "Email it", onClick: opts.onEmailFallback } : undefined,
    });
    return false;
  }

  // Fallback: unknown shape — assume success and let the endpoint's own toast run.
  return true;
}
